import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ihostpms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ihostpms_token');
      localStorage.removeItem('ihostpms_user');
      if (!window.location.pathname.includes('/login') && !window.location.hash?.includes('session_id')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  googleCallback: (sessionId) => api.post('/auth/google/callback', { session_id: sessionId }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Properties
export const propertiesAPI = {
  list: () => api.get('/properties'),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Bookings
export const bookingsAPI = {
  list: (params) => api.get('/bookings', { params }),
  get: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
};

// Availability
export const availabilityAPI = {
  get: (propertyId, params) => api.get(`/availability/${propertyId}`, { params }),
  update: (data) => api.post('/availability', data),
};

// Channels
export const channelsAPI = {
  list: () => api.get('/channels'),
  create: (data) => api.post('/channels', data),
  update: (id, data) => api.put(`/channels/${id}`, data),
  delete: (id) => api.delete(`/channels/${id}`),
  sync: (id) => api.post(`/channels/${id}/sync`),
  test: (data) => api.post('/channels/test', data),
};

// Reports
export const reportsAPI = {
  overview: (params) => api.get('/reports/overview', { params }),
  export: (params) => api.get('/reports/export', { params, responseType: params?.format === 'csv' ? 'blob' : 'json' }),
};

// Payments
export const paymentsAPI = {
  getPlans: () => api.get('/payments/public/plans'),
  createStripeCheckout: (data) => api.post('/payments/subscribe/stripe', data),
  createMpesaPayment: (data) => api.post('/payments/subscribe/mpesa', data),
  getStripeStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  getMpesaStatus: (transactionId) => api.get(`/payments/mpesa/status/${transactionId}`),
  getTransactions: () => api.get('/payments/transactions'),
  createBillingPortal: (returnUrl) => api.post('/payments/billing-portal', { return_url: returnUrl }),
};

// Host M-Pesa Configuration (for booking payments)
export const mpesaAPI = {
  getConfig: () => api.get('/settings/mpesa'),
  updateConfig: (data) => api.put('/settings/mpesa', data),
  testConfig: () => api.post('/settings/mpesa/test'),
};

// OTA Channel Integrations
export const otaAPI = {
  listChannels: () => api.get('/ota/channels'),
  getChannel: (channel) => api.get(`/ota/channels/${channel}`),
  connectChannel: (channel, data) => api.post(`/ota/channels/${channel}/connect`, data),
  updateChannel: (channel, data) => api.put(`/ota/channels/${channel}`, data),
  disconnectChannel: (channel) => api.delete(`/ota/channels/${channel}`),
  mapProperty: (channel, data) => api.post(`/ota/channels/${channel}/map-property`, data),
  unmapProperty: (channel, propertyId) => api.delete(`/ota/channels/${channel}/map-property/${propertyId}`),
  syncChannel: (channel, syncType) => api.post(`/ota/channels/${channel}/sync`, { sync_type: syncType }),
  getSyncStatus: (channel) => api.get(`/ota/channels/${channel}/sync-status`),
};

// Rates
export const ratesAPI = {
  get: (propertyId) => api.get(`/rates/${propertyId}`),
  update: (propertyId, data) => api.put(`/rates/${propertyId}`, data),
  calculate: (propertyId, params) => api.get(`/rates/${propertyId}/calculate`, { params }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Currencies
export const currenciesAPI = {
  list: () => api.get('/currencies'),
};

// Tenant Team
export const teamAPI = {
  list: () => api.get('/team'),
  invite: (data) => api.post('/team/invite', data),
  remove: (userId) => api.delete(`/team/${userId}`),
  updateRole: (userId, role) => api.put(`/team/${userId}/role`, { role }),
  updateStatus: (userId, status) => api.put(`/team/${userId}/status`, { status }),
};

// iCal Sync
export const icalSyncAPI = {
  listSubscriptions: () => api.get('/ical-sync/subscriptions'),
  createSubscription: (data) => api.post('/ical-sync/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/ical-sync/subscriptions/${id}`, data),
  deleteSubscription: (id) => api.delete(`/ical-sync/subscriptions/${id}`),
  syncNow: (id) => api.post(`/ical-sync/subscriptions/${id}/sync-now`),
  status: () => api.get('/ical-sync/sync-status'),
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: (data) => api.post('/push/unsubscribe', data),
  test: (data) => api.post('/push/test', data),
  status: () => api.get('/push/status'),
};

// Messaging
export const messagingAPI = {
  conversations: () => api.get('/messaging/conversations'),
  conversation: (bookingId) => api.get(`/messaging/conversations/${bookingId}`),
  send: (data) => api.post('/messaging/send', data),
  sendTemplate: (bookingId, templateId) => api.post(`/messaging/send-template?booking_id=${bookingId}&template_id=${templateId}`),
  generatePortal: (bookingId) => api.post(`/messaging/portal/generate/${bookingId}`),
  templates: () => api.get('/messaging/templates'),
  createTemplate: (data) => api.post('/messaging/templates', data),
  updateTemplate: (id, data) => api.put(`/messaging/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/messaging/templates/${id}`),
  fcmStatus: () => api.get('/messaging/fcm/status'),
  registerFCM: (data) => api.post('/messaging/fcm/register', data),
};

// Booking Widget API
export const widgetAPI = {
  create: (data) => api.post('/widgets', data),
  list: () => api.get('/widgets'),
  getByProperty: (propertyId) => api.get(`/widgets/property/${propertyId}`),
  get: (widgetId) => api.get(`/widgets/${widgetId}`),
  update: (widgetId, data) => api.put(`/widgets/${widgetId}`, data),
  delete: (widgetId) => api.delete(`/widgets/${widgetId}`),
};

// Cloudinary API
export const cloudinaryAPI = {
  status: () => api.get('/cloudinary/status'),
  signature: (params) => api.get('/cloudinary/signature', { params }),
};

// Payment Gateways API (per-property and host-level)
export const paymentGatewaysAPI = {
  // Property-level
  getPropertyConfig: (propertyId) => api.get(`/payment-gateways/property/${propertyId}`),
  updatePropertyConfig: (propertyId, data) => api.put(`/payment-gateways/property/${propertyId}`, data),
  // Host-level (tenant)
  getHostConfig: () => api.get('/payment-gateways/host'),
  updateHostConfig: (data) => api.put('/payment-gateways/host', data),
  // Pesapal specific
  registerPesapalIPN: () => api.post('/payment-gateways/pesapal/register-ipn'),
  testPesapal: () => api.post('/payment-gateways/pesapal/test'),
};

// Analytics & Demand Forecasting API
export const analyticsAPI = {
  // KPI Dashboard
  kpis: (params) => api.get('/analytics/kpis', { params }),
  // Demand Forecasting
  forecast: (propertyId, days = 90) => api.get(`/analytics/forecast/${propertyId}`, { params: { days } }),
  forecastSummary: () => api.get('/analytics/forecast-summary'),
  // Heatmap
  heatmap: (params) => api.get('/analytics/heatmap', { params }),
  // Trends
  trends: (params) => api.get('/analytics/trends', { params }),
  // Comparison
  compare: (propertyIds, params) => api.get('/analytics/compare', { params: { property_ids: propertyIds, ...params } }),
};

// WordPress Widget API
export const wordpressWidgetAPI = {
  create: (data) => api.post('/wordpress-widget', data),
  list: () => api.get('/wordpress-widgets'),
  get: (widgetId) => api.get(`/wordpress-widget/${widgetId}`),
  delete: (widgetId) => api.delete(`/wordpress-widget/${widgetId}`),
};

export default api;
